import jsPDF from "jspdf";
import "jspdf-autotable";
import { formatCurrency } from "@/utils/formatters";

export default function generatePdf(enterprise, budget, client, seller) {
  const user    = { ...this["auth/getUser"].user };
  const now     = new Date();
  const fmt2    = (n) => String(n).padStart(2, "0");
  const fmtDate = (d) => {
    const dt = new Date(d);
    return `${fmt2(dt.getDate())}/${fmt2(dt.getMonth() + 1)}/${dt.getFullYear()}`;
  };

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W   = 210;
  const M   = 14;
  const CW  = W - M * 2;

  const tblCfg = {
    headStyles: { fillColor: [230, 230, 230], textColor: [30, 30, 30], fontStyle: "bold", fontSize: 8 },
    bodyStyles: { fontSize: 8, textColor: [30, 30, 30] },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    margin: { left: M, right: M },
    theme: "grid",
  };

  // ── Header box ───────────────────────────────────────────────────
  const HEADER_H = 32;
  doc.setFillColor(245, 245, 245);
  doc.setDrawColor(200, 200, 200);
  doc.rect(M, M, CW, HEADER_H, "FD");

  const iM = M + 4;

  // Empresa — coluna esquerda
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(30, 30, 30);
  doc.text(enterprise?.name || "", iM, M + 8);

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80, 80, 80);

  const addr = enterprise?.address;

  let iy = M + 13;
  if (enterprise?.fantasyName) { doc.text(enterprise.fantasyName, iM, iy); iy += 4; }
  if (addr?.street)  { doc.text(`${addr.street}${addr.number ? `, ${addr.number}` : ""}`, iM, iy); iy += 4; }
  if (addr?.city)    { doc.text(`${addr.city}${addr.state ? ` - ${addr.state}` : ""}${addr.cep ? `  CEP: ${addr.cep}` : ""}`, iM, iy); iy += 4; }
  if (enterprise?.cnpj) { doc.text(`CNPJ: ${enterprise.cnpj}`, iM, iy); }

  // Separador vertical
  const sepX = M + CW * 0.62;
  doc.setDrawColor(210, 210, 210);
  doc.line(sepX, M + 3, sepX, M + HEADER_H - 3);

  // Orçamento — coluna direita
  const rX = sepX + 4;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(30, 30, 30);
  doc.text("ORÇAMENTO", rX, M + 8);

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80, 80, 80);
  doc.text(`Nº: ${budget.number_budget || ""}`,   rX, M + 13);
  doc.text(`Data: ${fmtDate(budget.date_budget)}`, rX, M + 18);
  doc.text(`Status: ${budget.status || ""}`,       rX, M + 23);
  doc.text(`Emitido por: ${user.name || ""}`,      rX, M + 28);

  let y = M + HEADER_H + 6;

  // ── Cliente / Vendedor ──────────────────────────────────────────
  doc.autoTable({
    ...tblCfg,
    startY: y,
    head: [["Cliente", "Vendedor"]],
    body: [[client || "—", seller || "—"]],
    columnStyles: {
      0: { cellWidth: CW / 2 },
      1: { cellWidth: CW / 2 },
    },
  });

  // ── Itens ────────────────────────────────────────────────────────
  y = doc.lastAutoTable.finalY + 6;

  const itemRows = (budget.budget_items || []).map((item) => [
    item.description_item || "",
    item.amount,
    formatCurrency(item.value_accounting_product),
    formatCurrency(item.discount_amount),
    formatCurrency(item.value_subtotal_product || item.value_budget_product),
  ]);

  doc.autoTable({
    ...tblCfg,
    startY: y,
    head: [["Produto", "Qtd", "Unit.", "Desconto", "Subtotal"]],
    body: itemRows,
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 16, halign: "center" },
      2: { cellWidth: 30, halign: "right" },
      3: { cellWidth: 30, halign: "right" },
      4: { cellWidth: 32, halign: "right" },
    },
  });

  // ── Totais ───────────────────────────────────────────────────────
  y = doc.lastAutoTable.finalY + 4;

  doc.autoTable({
    ...tblCfg,
    startY: y,
    head: [],
    body: [
      ["Subtotal", formatCurrency(budget.valor_products_total)],
      ["Desconto", `- ${formatCurrency(budget.value_descont_total)}`],
      ["Total", formatCurrency(budget.value_total_note)],
    ],
    columnStyles: {
      0: { cellWidth: CW - 50, halign: "right", fontStyle: "normal" },
      1: { cellWidth: 50,      halign: "right"  },
    },
    didParseCell(data) {
      if (data.row.index === 2) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fontSize  = 9;
      }
    },
  });

  // ── Formas de pagamento ──────────────────────────────────────────
  y = doc.lastAutoTable.finalY + 6;

  const payRows = (budget.budgetPaymentMethod || []).map((p) => [
    p.form_of_payment_description || p.formofpayment?.description || "—",
    p.payment_term?.description   || "—",
    formatCurrency(p.value_form_payment),
  ]);

  doc.autoTable({
    ...tblCfg,
    startY: y,
    head: [["Forma de Pagamento", "Condição", "Valor"]],
    body: payRows,
    columnStyles: {
      0: { cellWidth: 70 },
      1: { cellWidth: "auto" },
      2: { cellWidth: 35, halign: "right" },
    },
  });

  // ── Anotações ────────────────────────────────────────────────────
  if (budget.anotations) {
    y = doc.lastAutoTable.finalY + 6;
    doc.autoTable({
      ...tblCfg,
      startY: y,
      head: [["Observações"]],
      body: [[budget.anotations]],
    });
  }

  // ── Rodapé ───────────────────────────────────────────────────────
  const pageH   = 297;
  const footerY = pageH - 10;
  doc.setDrawColor(180, 180, 180);
  doc.line(M, footerY - 3, W - M, footerY - 3);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(140, 140, 140);
  doc.text(`Gerado em ${now.toLocaleString("pt-BR")}`, M, footerY);

  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.text(`Página ${i} / ${pages}`, W - M, footerY, { align: "right" });
  }

  window.open(URL.createObjectURL(doc.output("blob")), "_blank");
}
